import React, { useState } from 'react';
import { array, arrayOf, bool, func, shape, string, oneOf, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import logoImage from '../../assets/Sihmo_logo_color_white_bg-removebg-preview.png';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  propTypes,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { convertMoneyToNumber } from '../../util/currency';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import { isBookingProcess, resolveLatestProcessName } from '../../transactions/transaction';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  Modal,
  Button,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
} from './ListingPage.shared';
import { markNotificationRead, markGalleryViewed } from '../../util/api';
import { userNotifications } from '../../util/clientData';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';

import css from './ListingPage.module.css';
import classNames from 'classnames';
import IconCollection from '../../components/IconCollection/IconCollection.js';
import { TransformComponent, TransformWrapper, useControls } from 'react-zoom-pan-pinch';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard.js';
import { getUserRole } from '../../util/helper';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;
const { Money } = sdkTypes;

// Galleries this browser session has already reported as opened (no repeated requests).
const viewedGalleryRequests = new Set();

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [expandModal, setExpandModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [showMaxNumberModal, setShowMaxNumberModal] = useState(false);
  const [imageZoom, setimageZoom] = useState('');

  // Mark notification as read when customer visits this listing page
  React.useEffect(() => {
    const listingIdParam = props.params.id;
    const userRole = props.currentUser && getUserRole(props.currentUser);
    const notifications = userNotifications(props.currentUser);
    const hasNotification = notifications.some(n => n.listingId === listingIdParam);

    if (userRole === 'customer' && hasNotification) {
      const email = props.currentUser?.attributes?.email;
      if (email) {
        markNotificationRead({ email, listingId: listingIdParam }).catch(e =>
          console.error('Failed to mark notification read:', e)
        );
      }
    }
  }, [props.params.id, props.currentUser?.id?.uuid]);
  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews,
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    providerListings,
  } = props;
  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  // First time the client this gallery was created for opens it: record that,
  // so the photographer sees "Opened ..." on "Manage your listings". Done here
  // in the browser rather than in loadData - a direct link (e.g. from the
  // "your gallery is ready" email) is rendered on the server first, where this
  // API call can't run. The server only accepts the listing's own customer
  // (checked against the session) and only sets the time once.
  const viewedListingId = currentListing?.id?.uuid;
  const viewedPublicData = currentListing?.attributes?.publicData || {};
  const viewerId = currentUser?.id?.uuid;
  const viewerIsCustomer = !!currentUser && getUserRole(currentUser) === 'customer';
  React.useEffect(() => {
    // Whose gallery this is (the client's account id) is private data now, so the browser cannot
    // compare it with the viewer. Any signed-in customer who opens a gallery that has not been
    // opened yet asks the server once per session; the server checks the session against the
    // listing's client and answers 403 to everybody else.
    if (
      viewedListingId &&
      viewerId &&
      viewerIsCustomer &&
      !isDraftVariant &&
      !isPendingApprovalVariant &&
      !viewedPublicData.firstViewedAt &&
      !viewedGalleryRequests.has(viewedListingId)
    ) {
      viewedGalleryRequests.add(viewedListingId);
      markGalleryViewed({ listingId: viewedListingId }).catch(() => {});
    }
  }, [viewedListingId, viewerId, viewerIsCustomer, viewedPublicData.firstViewedAt]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const displayName = title && title.trim().split(' ');
  const firstName = displayName && displayName[0];
  const lastName =
    displayName && `${displayName.length > 1 ? displayName[displayName.length - 1] : ' '} `;
  const pricePackages = publicData && publicData?.pricePackages;
  const maxSelectImages = currentListing?.images?.length;
  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const transactionProcessAlias = publicData?.transactionProcessAlias || 'default-purchase/release-1';
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const processType = isBooking ? 'booking' : 'purchase';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const authorDescription = ensuredAuthor?.attributes?.profile?.publicData?.description;

  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const Controls = () => {
    const { zoomIn, zoomOut, resetTransform } = useControls();

    return (
      <div className={css.zoomTools}>
        <button className={css.zoomIn} onClick={() => zoomIn()}>
          <IconCollection name="zoomIn" />
        </button>
        <button className={css.zoomOut} onClick={() => zoomOut()}>
          <IconCollection name="zoomOut" />
        </button>
        {/* <button className={css.zoomCancel} onClick={() => resetTransform()}><IconCollection name="zoomCancel"/></button> */}
        <button className={css.zoomCancel} onClick={() => setIsModalOpen(false)}>
          <IconCollection name="zoomCancel" />
        </button>
      </div>
    );
  };

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;

    if (!isAuthenticated) {
      return history.push({
        pathname: '/login',
        state: {
          from: window.location.pathname,
        },
      });
    }

    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const schemaPriceMaybe = price
    ? {
      price: intl.formatNumber(convertMoneyToNumber(price), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      priceCurrency: price.currency,
    }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability =
    currentStock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

  const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));

  const currentListingImages = currentListing?.images
    ?.map(item => {
      return {
        id: item?.id?.uuid,
        url: item.attributes.variants?.['scaled-medium']?.url,
      };
    });

  const handleSelectAll = () => {
    if (selectedImages.length === maxSelectImages) {
      setSelectedImages([]);
    } else {
      const allImages = currentListingImages?.map(img => ({ imageUrl: img.url, id: img.id })) || [];
      setSelectedImages(allImages);
    }
  };

  const checkedImage = selectedImages?.map(data => data?.imageUrl);

  const handleCheckboxChange = (imageUrl, maxSelectImages, id) => {
    if (checkedImage.includes(imageUrl)) {
      setSelectedImages(prevSelectedImages =>
        prevSelectedImages.filter(img => img.imageUrl !== imageUrl)
      );
    } else if (checkedImage.length < maxSelectImages) {
      setSelectedImages(prevSelectedImages => [...prevSelectedImages, { imageUrl, id }]);
    } else {
      setShowMaxNumberModal(true);
    }
  };

  // Handle image click to open enlarged view. Used to be restricted to
  // mobile only (window.innerWidth <= 1023) - desktop customers browsing a
  // gallery before purchase had no way to zoom in at all.
  const handleImageClick = (imageUrl, e) => {
    e.preventDefault();
    e.stopPropagation();
    setimageZoom(imageUrl);
    setIsModalOpen(true);
  };

  const providerAccountCreated = ensuredAuthor?.attributes?.createdAt;
  const formatHostSince = dateValue => {
    const d = new Date(dateValue);
    return !isNaN(d.getTime()) ? `Host since ${d.getFullYear()}` : 'Host since ?';
  };

  const hostSince = formatHostSince(providerAccountCreated);

  const providerPublishedCount = providerListings.filter(
    listing => listing.attributes?.state === 'published'
  ).length;

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          availability: schemaAvailability,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.contentWrapperForProductLayout}>
          {currentListing.id && noPayoutDetailsSetWithOwnListing ? (
            <ActionBarMaybe
              className={css.actionBarForProductLayout}
              isOwnListing={isOwnListing}
              listing={currentListing}
              showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
            />
          ) : null}
          {currentListing.id ? (
            <ActionBarMaybe
              className={css.actionBarForProductLayout}
              isOwnListing={isOwnListing}
              listing={currentListing}
              editParams={{
                id: listingId.uuid,
                slug: listingSlug,
                type: listingPathParamType,
                tab: listingTab,
              }}
            />
          ) : null}
          <div className={css.bannerTitle}>
            <FormattedMessage id="ListingPage.yourPhoto" />
          </div>
          <div className={css.listingWrapper}>
            <div className={css.mainColumnForProductLayout}>
              <h3 className={css.imageTitle}>
                <FormattedMessage id="ListingPage.photos" />
              </h3>
              {/* Mobile: Select all photos above images */}
              <div className={css.mobileSelectAllRow}>
                <label className={css.mobileSelectAllLabel}>
                  <input
                    type="checkbox"
                    className={css.mobileSelectAllCheckbox}
                    checked={selectedImages?.length === maxSelectImages}
                    onChange={handleSelectAll}
                  />
                  <span><FormattedMessage id="Gallery.selectAllPhotos" /></span>
                </label>
                <span className={css.mobileCountBadge}>
                  {selectedImages?.length || 0}/{maxSelectImages}
                </span>
              </div>
              <div className={css.imagesWrapper}>
                <div className={css.imagesContainer}>
                  {currentListingImages?.length !== 0 &&
                    currentListingImages?.map((imageUrl, index) => (
                      <div
                        className={classNames(
                          css.section,
                          selectedImages.includes(imageUrl.url) && css.selectedImage
                        )}
                        key={index}
                      >
                        <img
                          className={css.listingImage}
                          src={imageUrl.url}
                          alt={`Image ${index + 1}`}
                          onContextMenu={e => e.preventDefault()}
                          onClick={(e) => handleImageClick(imageUrl.url, e)}
                        />
                        <div className={css.checkboxArea}>
                          <label
                            htmlFor={`Image ${index + 1}`}
                            className={css.checkboxLabel}
                          >
                            <span className={css.countNumber}>
                              {checkedImage.includes(imageUrl.url) && (
                                <span className={css.dotCircle}>
                                  <IconCollection name="TICK_ICON" />
                                </span>
                              )}
                            </span>
                          </label>
                          <input
                            type="checkbox"
                            checked={checkedImage.includes(imageUrl.url)}
                            onChange={() => handleCheckboxChange(imageUrl.url, maxSelectImages, imageUrl.id)}
                            id={`Image ${index + 1}`}
                            className={css.checkboxInput}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              <div>
                <div className={css.mobilePackage}>
                  <H4>
                    <FormattedMessage id="ListingPage.packages" />
                  </H4>
                  {pricePackages &&
                    pricePackages.length > 0 &&
                    pricePackages.map((item, index) => {
                      const price = new Money(item.amount, item.currency);
                      const { formattedPrice } = priceData(price, config.currency, intl);
                      const totalImage = Number(item.noOfImages);
                      return (
                        <div className={css.extraList} key={index}>
                          <span>
                            <FormattedMessage id="ListingPage.price" /> {totalImage}{' '}
                            {totalImage > 1 ? 'Images' : 'Image'} :
                          </span>
                          <span className={css.itemPrice}>
                            {' '}
                            <b>{formattedPrice}</b>
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
              <div className={css.mobileHeading}>
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              </div>
            </div>
            <div className={css.bottomContents}>
              {/* order form  */}
              <div className={css.orderColumnForProductLayout}>
                <OrderPanel
                  className={css.productOrderPanel}
                  listing={currentListing}
                  isOwnListing={isOwnListing}
                  onSubmit={handleOrderSubmit}
                  authorLink={
                    <NamedLink
                      className={css.authorNameLink}
                      name="ListingPage"
                      params={params}
                      to={{ hash: '#author' }}
                    >
                      {authorDisplayName}
                    </NamedLink>
                  }
                  // title={
                  //   <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  // }
                  // titleDesktop={
                  //   <H4 as="h1" className={css.orderPanelTitle}>
                  //     <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  //   </H4>
                  // }
                  payoutDetailsWarning={payoutDetailsWarning}
                  author={ensuredAuthor}
                  onManageDisableScrolling={onManageDisableScrolling}
                  onContactUser={onContactUser}
                  monthlyTimeSlots={monthlyTimeSlots}
                  onFetchTimeSlots={onFetchTimeSlots}
                  onFetchTransactionLineItems={onFetchTransactionLineItems}
                  lineItems={lineItems}
                  fetchLineItemsInProgress={fetchLineItemsInProgress}
                  fetchLineItemsError={fetchLineItemsError}
                  validListingTypes={config.listing.listingTypes}
                  marketplaceCurrency={config.currency}
                  dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                  marketplaceName={config.marketplaceName}
                  selectedImages={selectedImages}
                  isModalOpen={isModalOpen}
                  onSelectAll={handleSelectAll}
                  totalImages={maxSelectImages}
                />
                {(publicData?.clientDescription || description) ? (
                  <div className={css.descriptionCard}>
                    <p className={css.descriptionLabel}><FormattedMessage id="ListingPage.messageFromPhotographer" /></p>
                    <p className={css.descriptionText}>{publicData?.clientDescription || description}</p>
                  </div>
                ) : null}
                {/* {publicData?.message ? (
                  <div className={css.descriptionCard}>
                    <p className={css.descriptionLabel}>Message</p>
                    <p className={css.descriptionText}>{publicData.message}</p>
                  </div>
                ) : null} */}
              </div>

              <div className={css.aboutPhotographer}>
                <SectionAuthorMaybe
                  title={title}
                  listing={currentListing}
                  authorDisplayName={authorDisplayName}
                  onContactUser={onContactUser}
                  isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                  onCloseInquiryModal={() => setInquiryModalOpen(false)}
                  sendInquiryError={sendInquiryError}
                  sendInquiryInProgress={sendInquiryInProgress}
                  onSubmitInquiry={onSubmitInquiry}
                  currentUser={currentUser}
                  onManageDisableScrolling={onManageDisableScrolling}
                  hostSince={hostSince}
                  providerPublishedCount={providerPublishedCount}
                  authorDescription={authorDescription}
                />

                <div className={css.userReview}>
                  <IconCard type='quoteFilled' />
                  <div className={css.review}>{authorDescription}</div>
                  <div className={css.reviewName}>
                    - {ensuredAuthor?.attributes?.profile?.displayName}
                  </div>
                </div>
                {/* <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} /> */}
              </div>
            </div>
          </div>
        </div>
        <Modal
          isOpen={showMaxNumberModal}
          onClose={() => setShowMaxNumberModal(false)}
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <p>
            <FormattedMessage id="ListingPage.maxNoOfPackage" />
          </p>
        </Modal>
        {imageZoom && (
          <Modal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onManageDisableScrolling={onManageDisableScrolling}
            className={classNames(css.imageModal, expandModal && css.expandModalWrapper)}
          >
            <TransformWrapper initialScale={1}>
              {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                <>
                  <Controls />
                  <TransformComponent>
                    <img src={imageZoom} alt="test" />
                    <div className={css.waterMark}>
                      {/* <img className={css.logoImage} src={logoImage} alt="Logo" />
                      <img className={css.logoImage} src={logoImage} alt="Logo" />
                      <img className={css.logoImage} src={logoImage} alt="Logo" />
                      <img className={css.logoImage} src={logoImage} alt="Logo" />
                      <img className={css.logoImage} src={logoImage} alt="Logo" />
                      <img className={css.logoImage} src={logoImage} alt="Logo" /> */}
                    </div>
                  </TransformComponent>
                </>
              )}
            </TransformWrapper>
            {/* <div className={css.modalBottomContent}>
              <Button onClick={() => setIsModalOpen(false)} className={css.cancelButton}><FormattedMessage id="ListingPage.close" /></Button>
            </div> */}
            <div onClick={() => setExpandModal(!expandModal)} className={css.expandModal}>
              <IconCollection name="expandModal" />
            </div>
          </Modal>
        )}
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    providerListings,
  } = state.ListingPage;

  const { userListingRefs } = state.ProfilePage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    providerListings,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
